'use client'

import { useEffect } from 'react'
import { usePwaInstallStore, type BeforeInstallPromptEvent } from '@/stores/pwa-install-store'

/**
 * Captures Chrome's `beforeinstallprompt` so `InstallAppButton` can trigger it on demand instead
 * of Chrome's own mini-infobar — `preventDefault()` here is what suppresses that default UI.
 * Mounted once near the app root (`src/providers/index.tsx`); renders nothing.
 */
export function PwaInstallListener() {
  const setDeferredPrompt = usePwaInstallStore((state) => state.setDeferredPrompt)
  const setInstalled = usePwaInstallStore((state) => state.setInstalled)

  useEffect(() => {
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      // iOS Safari's non-standard installed-PWA flag.
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true
    if (isStandalone) {
      // Logged on purpose (not just dev) — this is the only way to confirm from a real device,
      // via remote `chrome://inspect` devtools, which branch fired without guessing.
      console.info('[pwa-install] already running standalone — treating as installed')
      setInstalled()
      return
    }

    function handleBeforeInstallPrompt(event: Event) {
      console.info('[pwa-install] beforeinstallprompt fired — capturing, suppressing default UI')
      event.preventDefault()
      setDeferredPrompt(event as BeforeInstallPromptEvent)
    }

    function handleAppInstalled() {
      console.info('[pwa-install] appinstalled fired')
      setInstalled()
    }

    console.info('[pwa-install] listening for beforeinstallprompt')
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [setDeferredPrompt, setInstalled])

  return null
}
